<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Chart, ArcElement, Tooltip } from 'chart.js';
import { Pie } from 'vue-chartjs';
import CardLayout from 'dashboard/components-next/CardLayout.vue';

Chart.register(ArcElement, Tooltip);

const props = defineProps({
  stats: {
    type: Object,
    default: () => ({}),
  },
});

const { t, locale } = useI18n();

// Rango fijo (1° al último día del mes en curso), no editable — criterio con
// el que SICARG maneja la cartera. Solo se muestra el nombre del mes.
const monthLabel = computed(() => {
  const formatter = new Intl.DateTimeFormat(locale.value || 'es', {
    month: 'long',
    year: 'numeric',
  });
  return formatter.format(new Date());
});

// SICARG opera en Argentina — montos siempre en ARS.
const formattedAmount = computed(() =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(props.stats.pago_realizado_monto || 0)
);

// Mismos colores que el Panel avanzado (SICARG MOD 3 v9) usa para los grupos
// de cobranza equivalentes — compromiso_futuro / gestion_sin_cobro /
// cobro_efectivo — para que la paleta sea consistente en todo el producto.
const COLORS = {
  promesa: 'rgba(139,92,246,0.85)',
  negociacion: 'rgba(245,158,11,0.85)',
  pago_realizado: 'rgba(16,185,129,0.85)',
  otros: 'rgba(148,163,184,0.6)',
};

const totalCount = computed(() => props.stats.total_count || 0);

// "Otros" completa el 100% del total gestionado en el mes — sin esta
// porción, el gráfico mostraría la proporción entre las 3 categorías
// nombradas en vez de su proporción real sobre el total, que es lo pedido.
const otrosCount = computed(() =>
  Math.max(
    totalCount.value -
      (props.stats.promesa_pago_count || 0) -
      (props.stats.negociacion_count || 0) -
      (props.stats.pago_realizado_count || 0),
    0
  )
);

const legendItems = computed(() => [
  {
    key: 'promesa',
    label: t('SICARG_DASHBOARD.STATS.PROMESA_PAGO'),
    value: props.stats.promesa_pago_count || 0,
    color: COLORS.promesa,
  },
  {
    key: 'negociacion',
    label: t('SICARG_DASHBOARD.STATS.NEGOCIACION'),
    value: props.stats.negociacion_count || 0,
    color: COLORS.negociacion,
  },
  {
    key: 'pago_realizado',
    label: t('SICARG_DASHBOARD.STATS.PAGO_REALIZADO'),
    value: props.stats.pago_realizado_count || 0,
    color: COLORS.pago_realizado,
  },
  {
    key: 'otros',
    label: t('SICARG_DASHBOARD.STATS.OTROS'),
    value: otrosCount.value,
    color: COLORS.otros,
  },
]);

const chartData = computed(() => ({
  labels: legendItems.value.map(item => item.label),
  datasets: [
    {
      data: legendItems.value.map(item => item.value),
      backgroundColor: legendItems.value.map(item => item.color),
      borderWidth: 0,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true },
  },
};
</script>

<template>
  <CardLayout>
    <h2 class="text-sm font-medium text-n-slate-12">
      {{ t('SICARG_DASHBOARD.STATS.TITLE') }}
      <span class="font-normal capitalize text-n-slate-10"
        >({{ monthLabel }})</span
      >
    </h2>

    <div v-if="totalCount" class="flex gap-4 items-center">
      <div class="relative shrink-0 size-28">
        <Pie :data="chartData" :options="chartOptions" />
      </div>
      <ul class="flex flex-col flex-grow gap-2">
        <li
          v-for="item in legendItems"
          :key="item.key"
          class="flex justify-between items-center text-sm"
        >
          <span class="flex gap-2 items-center text-n-slate-11">
            <span
              class="rounded-full size-2.5 shrink-0"
              :style="{ backgroundColor: item.color }"
            />
            {{ item.label }}
          </span>
          <span class="font-semibold text-n-slate-12">{{ item.value }}</span>
        </li>
      </ul>
    </div>
    <p v-else class="py-4 text-sm text-center text-n-slate-10">
      {{ t('SICARG_DASHBOARD.STATS.EMPTY') }}
    </p>

    <p
      v-if="stats.pago_realizado_count"
      class="text-sm font-medium text-n-slate-11"
    >
      {{ t('SICARG_DASHBOARD.STATS.PAGO_REALIZADO_MONTO') }}:
      <span class="text-n-slate-12">{{ formattedAmount }}</span>
    </p>
  </CardLayout>
</template>
